"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.TooltipContent = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _tooltipClose = _interopRequireDefault(require("./tooltip-close"));
var _tooltipTitle = _interopRequireDefault(require("./tooltip-title"));
var _dom = require("@floating-ui/dom");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/**
 * Render and position the tooltip content.
 *
 * @class TooltipContent
 * @private
 */
class TooltipContent extends _react.Component {
  constructor() {
    super(...arguments);
    this.contentRef = /*#__PURE__*/_react.default.createRef();
    this.arrowRef = /*#__PURE__*/_react.default.createRef();
    this.outsideClick = this.closeFromClickOutside.bind(this);
  }

  /**
   * Initializes @floating-ui updates, adds listeners for scroll and resize
   *
   * @private
   */
  floatingUI() {
    const referenceEl = this.props.anchorRef.current;
    const floatingEl = this.contentRef.current;
    if (!window.ResizeObserver) {
      this.updatePosition();
      return;
    }
    this.cleanup = (0, _dom.autoUpdate)(referenceEl, floatingEl, this.updatePosition.bind(this));
  }

  /**
   * After the initial render the tooltip will need to be re-rendered for
   * correct positioning in the DOM.
   *
   * @private
   */
  componentDidMount() {
    this.floatingUI();
    document.addEventListener('pointerdown', this.outsideClick);
  }

  /**
   * Provide the position and dimensions of the anchor element each time the
   * component is about to update the DOM.
   *
   * @private
   */
  componentDidUpdate() {
    this.floatingUI();
  }

  /**
   * Remove the attached click event listener and close any remaining tooltips.
   *
   * @private
   */
  componentWillUnmount() {
    document.removeEventListener('pointerdown', this.outsideClick);
    (this === null || this === void 0 ? void 0 : this.cleanup) && this.cleanup();
  }

  /**
   * Uses @floating-ui to position element.
   *
   * @private
   */
  updatePosition() {
    const referenceEl = this.props.anchorRef.current;
    const floatingEl = this.contentRef.current;
    const arrowEl = this.arrowRef.current;
    if (!referenceEl || !floatingEl) return;
    (0, _dom.computePosition)(referenceEl, floatingEl, {
      middleware: [(0, _dom.arrow)({
        element: arrowEl
      })]
    }).then(floating => {
      const {
        x,
        y,
        middlewareData,
        placement
      } = floating;
      floatingEl.dataset.placement = placement.split('-')[0];
      Object.assign(floatingEl.style, {
        left: `${x}px`,
        top: `${y}px`
      });
      if (middlewareData.arrow) {
        const {
          x: arrowX,
          y: arrowY
        } = middlewareData.arrow;
        Object.assign(arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : ''
        });
      }
    });
  }

  /**
   * Close event handler specifically for when a close was triggered by clicking
   * outside of the tooltip
   *
   * @param {Event} event React SyntheticMouseEnter event.
   * @private
   */
  closeFromClickOutside(event) {
    const element = this.contentRef.current;
    // If the event that triggered the outside close was hitting the anchor, "ignore" the event
    // We ignore it because we're deferring the process of closing the tooltip to the anchor's
    // click handler chain.
    const composedPath = typeof event.composedPath === 'function' && !Array.from(event.composedPath()).includes(element);
    if (this.props.persistent || !composedPath || !element || element.contains(event.target)) {
      return;
    }
    this.props.close();
  }

  /**
   * Render Tooltip content and store references to the wrapping div and the arrow.
   * Both are required for positional calculations and updates.
   *
   * @returns {ReactElement} div element with Tooltip content.
   * @private
   */
  render() {
    const props = this.props;
    const clsx = (0, _classnames.default)([props.className, 'tooltip', 'show']);
    return /*#__PURE__*/_react.default.createElement("div", {
      className: 'spaced-group'
    }, /*#__PURE__*/_react.default.createElement("div", {
      ref: this.contentRef,
      role: "tooltip",
      className: clsx,
      onMouseEnter: props.onMouseEnter,
      onMouseLeave: props.onMouseLeave
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: 'tooltip-arrow',
      ref: this.arrowRef
    }), /*#__PURE__*/_react.default.createElement("div", {
      className: 'tooltip-inner'
    }, props.hideClose || /*#__PURE__*/_react.default.createElement(_tooltipClose.default, {
      close: props.dismiss,
      ariaLabels: props.ariaLabels,
      className: 'tooltip-close'
    }), /*#__PURE__*/_react.default.createElement("span", {
      role: "status",
      "aria-live": "assertive"
    }, props.title && /*#__PURE__*/_react.default.createElement(_tooltipTitle.default, {
      className: 'tooltip-title'
    }, props.title), props.message))));
  }
}

/**
 * Define required and types of properties.
 *
 * @enum {Object} propTypes
 * @private
 */
exports.TooltipContent = TooltipContent;
TooltipContent.propTypes = {
  // @see https://stackoverflow.com/a/51127130
  contentRef: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.shape({
    current: _propTypes.default.any
  })]),
  anchorRef: _propTypes.default.object,
  root: _propTypes.default.object,
  message: _propTypes.default.node,
  title: _propTypes.default.node,
  close: _propTypes.default.func.isRequired,
  onMouseEnter: _propTypes.default.func,
  onMouseLeave: _propTypes.default.func,
  hideClose: _propTypes.default.bool,
  persistent: _propTypes.default.bool,
  dismiss: _propTypes.default.func,
  className: _propTypes.default.string,
  ariaLabels: _propTypes.default.shape({
    close: _propTypes.default.string
  })
};
var _default = exports.default = TooltipContent;